<template>
  <div
    class="md:container md:mx-auto mx-0 mt-6 ml-4 mr-4 rounded-full relative"
  >
    <div
      class="flex flex-row md:ml-20 md:mr-20"
      :style="`min-height: 80vh; height: 100%;`"
    >
      <!-- left side contains the users -->
      <div class="flex-auto md:w-3/4 bg-white md:rounded md:m-4 md:p-4">
        <r-table
          id="users-table"
          ref="usersTable"
          :items="filteredUsers"
          :rows-per-page="rowsPerPage"
          :label="$t('usersManagement.table.title')"
          :headers="usersHeaders"
          :dropdownOptions="dropdownOptions"
          @on-search="onSearchChange"
          @next-page="nextUserPage"
          @prev-page="prevUserPage"
          @new-item-req="openUserDialog"
        >
          <template slot="rows" slot-scope="{ items }">
            <tr v-for="(row, i) in items" :key="i">
              <!-- name -->
              <td class="flex flex-col capitalize md:table-cell">
                <div class="flex flex-row text-center">
                  {{ row.first_name }}
                  {{ row.last_name }}
                </div>

                <div class="block md:hidden text-sm">
                  {{ row.mobiles[0] }}
                </div>
              </td>

              <!-- email -->
              <td id="email-cell" class="hidden md:table-cell">
                <div v-for="email in row.emails" :key="email">
                  {{ email }}
                </div>
              </td>

              <!-- mobile -->
              <td id="mobile-cell" class="hidden md:table-cell">
                <div v-for="mobile in row.mobiles" :key="mobile">
                  {{ mobile }}
                </div>
              </td>

              <!-- row action -->
              <td>
                <!-- md view -->
                <div class="hidden md:table-cell flex flex-row leading-none">
                  <!-- edit employee -->
                  <r-button
                    id="open-update-dialog-md-view"
                    class="flex-1 mr-4"
                    @click="openUserDialog('update', row)"
                  >
                    {{ $t("buttons.edit") }}
                  </r-button>

                  <!-- remove employee -->
                  <r-button
                    id="delete-user-md-view"
                    class="flex-1"
                    color="error"
                    :disabled="checkIfUserDeleteDisable(row)"
                    @click="toggleDeleteDialog(row)"
                  >
                    {{ $t("buttons.delete") }}
                  </r-button>
                </div>

                <!-- mobile view -->
                <div class="block md:hidden flex leading-none text-sm">
                  <div class="flex-auto" />
                  <div
                    id="open-update-dialog-mobile-view"
                    class="m-1 p-2 bg-r-primary text-white rounded-full"
                    @click="openUserDialog('update', row)"
                  >
                    <font-awesome-icon icon="pen" />
                  </div>

                  <div
                    id="delete-user-mobile-view"
                    :class="deleteIconClasses(row)"
                    @click="toggleDeleteDialog(row)"
                  >
                    <font-awesome-icon icon="trash-alt" />
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </r-table>
      </div>

      <!-- dialogs -->
      <!-- crm dialog -->
      <r-dialog id="crm-dialog" v-model="crmDialog.open">
        <div
          v-if="crmDialog.mode === 'selectCRM'"
          class="flex flex-col items-center bg-white rounded shadow-lg h-full p-4"
        >
          <!-- title -->
          <div class="font-bold w-full">
            {{ $t("integrations.crm.selectCRM") }}
          </div>

          <!-- options -->
          <div class="flex flex-row items-center justify-center my-6">
            <div
              v-for="(image, crmName) in images"
              :key="crmName"
              class="m-2 p-2"
            >
              <div
                class="border border-solid border-r-secondary rounded w-56 h-56 flex items-center justify-center p-4 hover:bg-r-secondary hover:bg-opacity-25 cursor-pointer"
                @click="setSelectedCRM(crmName)"
              >
                <img :src="image" />
              </div>
            </div>
          </div>
        </div>
        <div
          v-else-if="
            crmDialog.mode === 'integrate' &&
            crmDialog.selectedCRM === 'hubspot'
          "
          class="flex flex-col items-center bg-white rounded shadow-lg h-full p-4"
        >
          <!-- title -->
          <div class="font-bold w-full">
            {{ $t("integrations.crm.giveCarlaAccess") }}
          </div>

          <!-- CRM logo -->
          <img :src="images.hubspot" class="w-56 mb-6 mt-5" />

          <!-- Description -->
          <div class="mt-4" v-html="$t('integrations.crm.hubspot.guide')" />

          <!-- Docs Hubspot api link -->
          <div class="block mt-5">
            {{ $t("integrations.crm.hubspot.docs.part1") }}
            <a
              class="underline text-blue-800"
              href="https://knowledge.hubspot.com/integrations/how-do-i-get-my-hubspot-api-key"
            >
              {{ $t("integrations.crm.hubspot.docs.link") }}
            </a>
            {{ $t("integrations.crm.hubspot.docs.part2") }}
          </div>

          <div
            class="h-full w-full flex flex-col justify-center items-center py-10"
          >
            <!-- api key input -->
            <r-text-field
              id="crm-apikey-input"
              :label="$t('integrations.crm.apiKey')"
              v-model="crmDialog.apiKey"
              class="w-full py-2"
            />
          </div>

          <div class="flex flex-row w-full justify-end">
            <r-button id="crm-apikey-save-button" @click="saveApiKey">
              {{ $t("buttons.save") }}
            </r-button>
          </div>
        </div>
        <div
          v-else-if="
            crmDialog.mode === 'integrate' &&
            crmDialog.selectedCRM === 'salesforce'
          "
          class="flex flex-col items-center bg-white rounded shadow-lg h-full p-4"
        >
          <!-- title -->
          <div class="font-bold w-full">
            {{ $t("integrations.crm.giveCarlaAccess") }}
          </div>

          <!-- crm logo -->
          <img :src="images.salesforce" class="w-56 mb-6 mt-5" />

          <!-- Description -->
          <div class="mt-4" v-html="$t('integrations.crm.salesforce.guide')" />

          <!-- sign in with salesforce -->
          <r-button
            id="sign-in-with-salesforce-button"
            text
            class="flex item-center justify-center md:w-64 w-full my-8"
            @click="continueWithSalesforce"
          >
            <!-- icon -->
            <img src="@/assets/icons/salesforce.png" class="w-5 mr-2" />

            <!-- content -->
            <div>
              {{ $t("buttons.continueWithSalesforce") }}
            </div>
          </r-button>
        </div>
      </r-dialog>

      <!-- user's dialog -->
      <r-dialog
        id="user-dialog"
        v-model="userDialog.open"
        @on-close="closeUserDialog"
      >
        <!-- Tabs - Manual/CSV -->
        <div
          v-if="!this.isEditModeUserDialog"
          class="w-80"
          style="margin-bottom: -1px"
        >
          <ul class="flex cursor-pointer shadow-md">
            <li :class="manualTabClasses" @click="onTabClick('manual')">
              {{ $t("forms.tabs.manual") }}
            </li>
            <li :class="csvTabClasses" @click="onTabClick('csv')">
              {{ $t("forms.tabs.csvs") }}
            </li>
          </ul>
        </div>

        <div class="flex flex-col bg-white h-full w-full rounded shadow-lg p-4">
          <!-- Add using CSV -->
          <div v-if="csvDialog.open">
            <rele-upload-csv @close-csv="closeUserDialog" />
          </div>
          <!-- Add Manual -->
          <div v-else>
            <!-- title -->
            <div class="font-bold">
              {{ $t("usersManagement.user") }}
            </div>

            <!-- form -->
            <div class="h-full flex flex-col justify-center md:py-8">
              <r-text-field
                v-model="userDialog.firstName"
                class="mb-8"
                id="first-name-input"
                ref="firstNameInput"
                validation="firstName"
                :label="$t('forms.firstname')"
              />

              <r-text-field
                v-model="userDialog.lastName"
                class="mb-8"
                id="last-name-input"
                ref="lastNameInput"
                validation="lastName"
                :label="$t('forms.lastname')"
              />

              <r-text-field
                id="email-input"
                ref="emailInput"
                v-model="userDialog.email"
                class="mb-8"
                validation="email"
                :label="$t('forms.email')"
              />

              <r-select
                id="bot-lang-select"
                v-model="userDialog.botLang"
                class="mb-8"
                :label="$t('forms.botLang')"
                :options="botLangOptions"
              />

              <vue-phone-number-input
                id="mobile-input"
                :disabled="isEditModeUserDialog"
                :default-country-code="countryCode"
                v-model="mobile"
                :error="isMobileError"
                class="outline-none"
                @update="handleMobileUpdate"
              />
              <label
                v-show="shouldRenderMobileError"
                class="text-red-600 text-xs transition-all"
              >
                {{ $t(`errors.validateMobile`) }}
              </label>
            </div>

            <!-- buttons -->
            <div class="flex flex-row w-full justify-end mt-10 md:mt-0">
              <!-- cancel -->
              <r-button
                id="user-close-dialog"
                class="mr-4"
                text
                color="black"
                @click="closeUserDialog"
              >
                {{ $t("buttons.cancel") }}
              </r-button>

              <!-- done -->
              <r-button
                id="user-save-dialog"
                @click="saveUser"
                :disabled="isFormDisable"
                :loading="userDialog.loading"
              >
                {{ $t("buttons.save") }}
              </r-button>
            </div>
          </div>
        </div>
      </r-dialog>

      <!-- Delete dialog -->
      <r-dialog id="delete-dialog" v-model="deleteDialog.open">
        <div class="flex flex-col bg-white h-full w-full rounded shadow-lg p-4">
          <!-- title -->
          <div class="font-bold">
            {{ $t("usersManagement.deleteDialog.title") }}
          </div>

          <!-- form -->
          <div
            class="h-full flex flex-col justify-center md:py-8 mt-10 md:mt-0"
          >
            {{
              $t("usersManagement.deleteDialog.description", {
                name: this.deleteDialog.name
              })
            }}
          </div>

          <!-- buttons -->
          <div class="flex flex-row w-full justify-end mt-10 md:mt-0">
            <!-- cancel -->
            <r-button
              id="user-close-dialog"
              class="mr-4"
              text
              color="black"
              @click="toggleDeleteDialog"
            >
              {{ $t("buttons.cancel") }}
            </r-button>

            <!-- done -->
            <r-button
              id="user-save-dialog"
              @click="deleteUser"
              color="error"
              :loading="deleteDialog.loading"
            >
              {{ $t("buttons.delete") }}
            </r-button>
          </div>
        </div>
      </r-dialog>
    </div>
    <r-footer-notification
      v-model="footerNotification.open"
      :text="footerNotification.text"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import axios from "axios"
import { validateEmail, validateFirstName, validateLastName } from "@/utils"
import salesforceAPI from "@/api/salesforce"

import hubspotUrl from "@/assets/icons/hubspot.png"
import salesforceUrl from "@/assets/icons/salesforce.png"

const NOTIFICATION_TIMEOUT_INTERVAL = 3000
/**
 * Define the main page
 */
export default {
  // component state
  data() {
    return {
      // filtered users
      filteredUsers: [],

      // footer Notification
      footerNotification: {
        open: false,
        text: "",
        timer: null
      },

      // tmp mobile
      mobile: "",
      countryCode: "US",
      defaultCountryCode: "",

      // images
      images: {
        hubspot: hubspotUrl,
        salesforce: salesforceUrl
      },

      crmDialog: {
        selectedCRM: "",
        apiKey: "",
        open: false,
        mode: "selectCRM"
      },

      userDialog: {
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        id: "",
        botLang: "INHERIT",
        loading: false,
        open: false,
        isMobileValid: false
      },

      csvDialog: {
        open: false
      },

      deleteDialog: {
        open: false,
        userId: "",
        name: "",
        loading: false
      },

      botLangOptions: [
        {
          value: "INHERIT",
          display: this.$t("botOptions.inherit")
        },
        {
          value: "EN",
          display: this.$t("botOptions.en")
        },
        {
          value: "ES",
          display: this.$t("botOptions.es")
        },
        {
          value: "FR",
          display: this.$t("botOptions.fr")
        },
        {
          value: "GR",
          display: this.$t("botOptions.gr")
        },
        {
          value: "PT",
          display: this.$t("botOptions.pt")
        }
      ],

      // users table headers
      usersHeaders: [
        {
          label: "usersManagement.table.name",
          classes: ""
        },
        {
          label: "usersManagement.table.email",
          classes: "hidden md:table-cell"
        },
        {
          label: "usersManagement.table.phone",
          classes: "hidden md:table-cell"
        },
        {
          label: "usersManagement.table.actions",
          classes: "hidden md:table-cell"
        }
      ],

      // table dropdown options
      dropdownOptions: [
        {
          key: "exportCsv",
          label: this.$t("buttons.exportCsv"),
          callback: this.exportUsers
        }
      ]
    }
  },

  // calculated props
  computed: {
    ...mapGetters({
      // auth attributes
      user: "auth/getUser",

      jwt: "auth/getIdToken",

      userDoc: "users/getDoc",

      // users list
      users: "users/get",

      // org attribute
      org: "orgs/getDoc"
    }),

    /**
     * Returns the notification height
     */
    notificationHeight() {
      if (!process.server && document) {
        const elem = document.getElementById("global-notification-bar")
        if (elem) {
          return elem.clientHeight + "px"
        }
      }

      return "0px"
    },

    /**
     * Returns rows per page
     */
    rowsPerPage() {
      console.log("Screen height: ", window.screen.height)
      if (window.screen.height < 900) {
        return 8
      } else if (window.screen.height >= 900 && window.screen.height < 1080) {
        return 11
      } else return 14
    },

    /**
     * Get selected org
     */
    selectedOrg() {
      const { orgs = [] } = this.userDoc
      return orgs.length ? orgs[0] : ""
    },

    /**
     * Returns true if error need to be shown on mobile
     */
    isMobileError() {
      return (
        Boolean(this.userDialog.mobile) &&
        (!this.userDialog.isMobileValid || this.checkIfUserDuplicate) &&
        Boolean(!this.userDialog.id) // not on edit mode
      )
    },

    /**
     * Should render mobile error
     */
    shouldRenderMobileError() {
      return (
        this.userDialog.isMobileValid &&
        this.checkIfUserDuplicate &&
        Boolean(!this.userDialog.id) // not on edit mode
      )
    },

    /**
     * Returns the csv file name to export
     */
    csvFileName() {
      const { nameLower = "" } = this.org

      return `${nameLower.replace(/[-| |.|]/g, "_")}_users_${
        this.cleanISODate
      }.csv`
    },

    /**
     * clean isoDate
     */
    cleanISODate() {
      return new Date().toISOString().replace(/[.]/g, "_")
    },

    /**
     * Search by list
     */
    searchByList() {
      return ["first_name", "last_name", "emails", "mobiles"]
    },

    /**
     * Returns true if there is a user with same mobile
     */
    checkIfUserDuplicate() {
      // checks if user with same mobile exists
      const user = (this.users || []).find((user) => {
        const [mobile] = user.mobiles
        return mobile === (this.userDialog.mobile || "").substring(1)
      })

      // returns true is exists
      return Boolean(Object.keys(user || {}).length)
    },

    /**
     * Check if organization have any CRM connected
     */
    crmConnection() {
      console.log("this user doc", this.userDoc)
      if (this.org && this.org.hubspot && this.org.hubspot.api_key) {
        return {
          crm: "hubspot",
          apiKey: this.org.hubspot.api_key
        }
      } else if (
        this.org &&
        this.userDoc &&
        this.userDoc.salesforce &&
        this.userDoc.salesforce[this.org.id]
      ) {
        return {
          crm: "salesforce",
          apiKey: ""
        }
      }

      return null
    },

    /**
     * Returns the tab classes
     */
    manualTabClasses() {
      // define base classes
      return {
        "w-40": true,
        "py-2": true,
        "px-6": true,
        "bg-white": true,
        "text-gray-500": this.csvDialog.open,
        "bg-gray-200": this.csvDialog.open,
        "rounded-t-lg": true,
        "shadow-inner": true
      }
    },

    /**
     * Returns the tab classes
     */
    csvTabClasses() {
      // define base classes
      return {
        "w-40": true,
        "py-2": true,
        "px-6": true,
        "bg-white": true,
        "text-gray-500": !this.csvDialog.open,
        "bg-gray-200": !this.csvDialog.open,
        "rounded-t-lg": true,
        "shadow-inner": true
      }
    },

    /**
     * Is form button disable
     */
    isFormDisable() {
      const { email, firstName, lastName } = this.userDialog
      const isEdit = this.isEditModeUserDialog
      return (
        !this.userDialog.isMobileValid ||
        (this.checkIfUserDuplicate && !isEdit) ||
        !validateEmail(email) ||
        !validateFirstName(firstName) ||
        !validateLastName(lastName) ||
        !firstName ||
        !lastName
      )
    },

    /**
     * return true if user dialog
     * opened on edit mode
     */
    isEditModeUserDialog() {
      return Boolean(this.userDialog.id)
    }
  },

  /**
   * Bind on load
   */
  mounted() {
    if (((this.userDoc || {}).orgs || []).length) {
      this.bindUsers()
      this.setLocale()
    }

    this.setInfoFromIP()
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  // watch props
  watch: {
    // monitor the user access to bind data
    userDoc: {
      immediate: true,
      handler() {
        if (((this.userDoc || {}).orgs || []).length) {
          this.bindUsers()
          this.setLocale()
        }
      }
    },

    org: {
      immediate: true,
      handler() {
        if (this.org?.hubspot?.api_key) {
          this.$set(this.crmDialog, "selectedCRM", "hubspot")
          this.$set(this.crmDialog, "mode", "integrate")
        }
      }
    },

    users: {
      immediate: true,
      handler() {
        if ((this.users || []).length) {
          this.filteredUsers = [...this.users]
        }
      }
    }
  },

  // component functions
  methods: {
    /**
     * Signin with salesforce.
     */
    async continueWithSalesforce() {
      try {
        const api = new salesforceAPI(this.jwt)
        const { authUrl } = await api.notify("auth_url")
        location.replace(authUrl)
      } catch (error) {
        this.notifyError({
          title: this.$t("errors.unableToLoginWithSalesforce"),
          message: error.message
        })
      }
    },

    /**
     * Set the selected CRM on the dialog
     */
    setSelectedCRM(name) {
      this.$set(this.crmDialog, "selectedCRM", name)
      this.$set(this.crmDialog, "mode", "integrate")
    },

    /**
     * Delete icon classes
     */
    deleteIconClasses(user) {
      const isDisable = user.id === this.userDoc?.id

      return {
        "m-1": true,
        "p-2": true,
        "bg-r-error": !isDisable,
        "bg-gray-500": isDisable,
        "text-white": true,
        "rounded-full": true
      }
    },

    /**
     * Returns true if user delete disable
     */
    checkIfUserDeleteDisable(user) {
      return user?.id === this.userDoc?.id
    },

    /**
     * Delete user
     */
    async deleteUser() {
      // set loading
      this.$set(this.deleteDialog, "loading", true)

      try {
        // delete user
        await this.$store.dispatch("users/delete", this.deleteDialog.userId)

        this.$refs.usersTable.onRowDelete()
        this.toggleDeleteDialog()
        this.$set(this.deleteDialog, "loading", false)
      } catch (error) {
        this.notifyError({
          title: this.$t("errors.unableToDeleteUser"),
          message: error.message
        })
      }
    },

    /**
     * Set locales to console
     */
    setLocale() {
      this.$locale =
        (this.userDoc?.console_lang || "").toLowerCase() ||
        localStorage.getItem("console-locale") ||
        "en"
    },

    /**
     * Extract the formatted version of the number
     */
    handleMobileUpdate(v) {
      this.countryCode = v.countryCode
      this.$set(this.userDialog, "mobile", v.formattedNumber)
      this.$set(this.userDialog, "isMobileValid", v.isValid)
    },

    /**
     * Close the user dialog
     */
    closeUserDialog() {
      this.userDialog = {
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        id: "",
        open: false,
        loading: false,
        botLang: "INHERIT",
        isMobileValid: false
      }

      this.mobile = ""
      this.countryCode = "US"

      // reset csv
      this.csvDialog = {
        open: false
      }

      // inputs to defaults
      this.$refs.emailInput.onCloseDialog()
      this.$refs.firstNameInput.onCloseDialog()
      this.$refs.lastNameInput.onCloseDialog()
    },

    /**
     * toggle Delete Dialog
     */
    toggleDeleteDialog(user = {}) {
      const isDisable = user.id === this.userDoc?.id
      if (!isDisable) {
        const { id = "", first_name = "", last_name = "" } = user

        this.$set(this.deleteDialog, "open", !this.deleteDialog.open)
        this.$set(this.deleteDialog, "name", `${first_name} ${last_name}`)
        this.$set(this.deleteDialog, "userId", id)
      }
    },

    /**
     * Open the user dialog
     */
    async openUserDialog(type, user) {
      if (type === "update") {
        const mobile = (user.mobiles || [])[0] || ""
        const email = (user.emails || [])[0] || ""
        // const botLang =
        //   user.bot_lang === "INHERIT" ? this.org?.bot_lang : user.bot_lang

        this.$set(this.userDialog, "id", user.id)
        this.$set(this.userDialog, "firstName", user.first_name)
        this.$set(this.userDialog, "lastName", user.last_name)
        this.$set(this.userDialog, "mobile", mobile)
        this.$set(this.userDialog, "email", email)
        this.$set(this.userDialog, "botLang", user.bot_lang)
        this.mobile = "+" + mobile
        this.countryCode = user.mobiles_country_code
        console.log("open user dialog in update")
      } else {
        this.countryCode = this.defaultCountryCode
        console.log("open user dialog in new")
      }

      this.$set(this.userDialog, "open", true)
    },

    /**
     * Save the API key to the organization
     */
    saveApiKey() {
      this.$store.dispatch("orgs/update", {
        id: this.selectedOrg,
        data: {
          [this.crmDialog.selectedCRM]: {
            api_key: this.crmDialog.apiKey
          }
        }
      })

      this.closeCRMDialog()
    },

    /**
     * Returns the bot lang from the cookies
     */
    getBotLang() {
      const matches = document.cookie.match(
        new RegExp(
          "(?:^|; )" + "rb_user_locale".replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + "=([^;]*)" // eslint-disable-line
        )
      )

      return matches ? decodeURIComponent(matches[1]) : ""
    },

    /**
     * Save user
     */
    async saveUser() {
      try {
        const isEdit = this.isEditModeUserDialog
        this.$set(this.userDialog, "loading", true)

        if (isEdit) {
          await this.updateUser()
        } else {
          await this.createUser()
        }

        this.showFooterNotification(isEdit)
        this.closeUserDialog()
      } catch (error) {
        this.notifyError({
          title: this.$t("errors.unableToCreateUser"),
          message: error.message
        })
      }
    },

    /**
     * Update user
     */
    async updateUser() {
      const botLangs = {
        en: 1,
        es: 2,
        fr: 3,
        gr: 4,
        inherit: 5
      }
      const adminBotLang = this.userDialog.botLang.toLowerCase() || "en"

      await this.$store.dispatch("users/update", {
        id: this.userDialog.id,
        data: {
          lastName: this.userDialog.lastName,
          firstName: this.userDialog.firstName,
          mobiles: [this.userDialog.mobile.slice(1)],
          emails: [this.userDialog.email],
          mobiles_country_code: this.countryCode,
          botLang: botLangs[adminBotLang]
        },
        options: { ignore_flatten: true }
      })
    },

    /**
     * Create user
     */
    async createUser() {
      const botLangs = {
        en: 1,
        es: 2,
        fr: 3,
        gr: 4,
        inherit: 5
      }
      const adminBotLang = this.userDialog.botLang.toLowerCase() || "en"

      await this.$store.dispatch("users/add", {
        botLang: botLangs[adminBotLang],
        lastName: this.userDialog.lastName,
        firstName: this.userDialog.firstName,
        mobiles: [this.userDialog.mobile.slice(1)],
        emails: [this.userDialog.email],
        orgs: [this.selectedOrg],
        mobiles_country_code: this.countryCode
      })
    },

    /**
     * Show Footer Notification
     */
    showFooterNotification(isEdit) {
      // define label
      const localLabel = isEdit ? "editSuccess" : "createSuccess"

      // open notification
      this.$set(this.footerNotification, "open", true)
      this.$set(
        this.footerNotification,
        "text",
        this.$t(`notifications.${localLabel}`, {
          name: `${this.userDialog.firstName} ${this.userDialog.lastName}`
        })
      )

      // set timeout for closing notification
      this.$set(this.footerNotification, "timer", null)
      this.$set(
        this.footerNotification,
        "timer",
        setTimeout(() => {
          this.$set(this.footerNotification, "open", false)
        }, NOTIFICATION_TIMEOUT_INTERVAL)
      )
    },

    /**
     * Opens the CRM dialog
     */
    openCRMDialog(type) {
      if (type === "update") {
        this.crmDialog.apiKey = this.org[this.crmDialog.selectedCRM].api_key
        console.log("open crm dialog - update")
      } else {
        console.log("open crm dialog - new")
      }

      this.$set(this.crmDialog, "open", true)
    },

    /**
     * Close the CRM dialog
     */
    closeCRMDialog() {
      this.$set(this.crmDialog, "open", false)
      this.$set(this.crmDialog, "apiKey", "")
    },

    /**
     * Bind users data
     */
    bindUsers() {
      this.$store.dispatch("users/bind", {
        constraints: [["orgs", "array-contains", this.selectedOrg]],
        limit: 500,
        orderBy: ["first_name"]
      })
    },

    exportUsers() {
      // get rows
      const rows = this.buildCsvMatrix()

      // build csv file
      let csvContent =
        "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n")
      var encodedUri = encodeURI(csvContent)
      var link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", this.csvFileName)
      document.body.appendChild(link)

      // fired on click
      link.click()
    },

    buildCsvMatrix() {
      let rows = []

      // build csv matrix
      this.users.forEach((user, i) => {
        const data = {
          firstName: user.first_name,
          lastName: user.last_name,
          email: user.emails[0],
          mobile: user.mobiles[0],
          countryCode: user.mobiles_country_code,
          botLang: user.bot_lang
        }

        // add headers
        if (i === 0) {
          rows.push(Object.keys(data))
        }

        // add values
        rows.push(Object.values(data))
      })

      // return rows
      return rows
    },

    /**
     * On Search change
     */
    onSearchChange(searchQuery) {
      // format search query to lowe case
      const searchFormat = searchQuery.toLowerCase()

      if (searchQuery) {
        // filter items by seach keys
        this.filteredUsers = this.users.filter((item) => {
          let isFiltered = false
          this.searchByList.forEach((key) => {
            const toSearch = Array.isArray(item[key]) ? item[key][0] : item[key]
            isFiltered =
              isFiltered || toSearch.toLowerCase().includes(searchFormat)

            // if there is a match, return true
            if (isFiltered) return isFiltered
          })

          return isFiltered
        })
      } else {
        this.filteredUsers = [...this.users]
      }
    },

    /**
     * Set country code by IP address
     */
    async setInfoFromIP() {
      // resolve promise and get data from ip
      try {
        const loc = await axios.get("https://json.geoiplookup.io/")
        // const loc = await locProm
        this.defaultCountryCode = loc.data?.country_code || "US"
      } catch (e) {
        this.defaultCountryCode = "US"
      }
    },

    /**
     * toggel tab
     */
    onTabClick(tab) {
      // define if should load csv tab
      const isCsvTab = tab === "csv"

      // set dialogs
      this.$set(this.csvDialog, "open", isCsvTab)
    },

    /**
     * Toggle to next users page
     */
    nextUserPage(token) {
      console.log("next page", token)
    },

    /**
     * Toggle to prev users page
     */
    prevUserPage(token) {
      console.log("prev page", token)
    }
  }
}
</script>
